import React from 'react';

interface Mention {
  id: string;
  name: string;
  startIndex: number;
  endIndex: number;
}

interface ParsedContent {
  text: string;
  mentions: Mention[];
}

/**
 * Mention format: @[FirstName LastName]
 * Example: "Hey @[John Doe], check this out"
 * Also supports legacy format: @(userId:FirstName LastName)
 */
export const parseMentions = (text: string): ParsedContent => {
  // Support both new @[Name] and legacy @(id:Name) formats
  const newMentionRegex = /@\[([^\]]+)\]/g;
  const legacyMentionRegex = /@\(([^:]+):([^)]+)\)/g;
  const mentions: Mention[] = [];
  let match;

  while ((match = newMentionRegex.exec(text)) !== null) {
    mentions.push({
      id: '',
      name: match[1],
      startIndex: match.index,
      endIndex: newMentionRegex.lastIndex,
    });
  }

  while ((match = legacyMentionRegex.exec(text)) !== null) {
    mentions.push({
      id: match[1],
      name: match[2],
      startIndex: match.index,
      endIndex: legacyMentionRegex.lastIndex,
    });
  }

  return { text, mentions };
};

/**
 * Unescape common HTML entities that may have been stored by earlier
 * double-encoding (frontend escapeHtml + backend bleach).  Only handles
 * the handful of entities the old escapeHtml produced.
 */
const unescapeHtmlEntities = (text: string): string =>
  text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/&#39;/g, "'");

/**
 * Render message with mentions as blue text.
 * Supports both @[Name] and legacy @(userId:Name) formats.
 */
export const renderMessageWithMentions = (message: string) => {
  // Fix any HTML entities that were stored by the old double-escaping bug
  const clean = unescapeHtmlEntities(message);
  // Combined regex: match @[Name] or @(id:Name)
  const mentionRegex = /@\[([^\]]+)\]|@\(([^:]+):([^)]+)\)/g;
  const parts: (React.ReactNode)[] = [];
  let lastIndex = 0;
  let match;
  let mentionCount = 0;

  while ((match = mentionRegex.exec(clean)) !== null) {
    // Add text before mention
    if (match.index > lastIndex) {
      parts.push(
        <span key={`text-${lastIndex}`}>
          {clean.substring(lastIndex, match.index)}
        </span>
      );
    }

    // Get name from either format
    const userName = match[1] || match[3];
    parts.push(
      <span
        key={`mention-${mentionCount}`}
        className="text-info-600 dark:text-info-400 font-medium"
      >
        {userName}
      </span>
    );
    mentionCount++;

    lastIndex = mentionRegex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < clean.length) {
    parts.push(
      <span key={`text-end`}>
        {clean.substring(lastIndex)}
      </span>
    );
  }

  // If no mentions found, just return the clean message
  if (parts.length === 0) {
    return <span>{clean}</span>;
  }

  return <>{parts}</>;
};

/**
 * Extract user mentions from a message
 * Supports both @[Name] and legacy @(userId:Name) formats
 */
export const extractMentions = (text: string): Array<{ userId: string; userName: string }> => {
  const mentions: Array<{ userId: string; userName: string }> = [];
  let match;

  // New format: @[Name]
  const newRegex = /@\[([^\]]+)\]/g;
  while ((match = newRegex.exec(text)) !== null) {
    mentions.push({ userId: '', userName: match[1] });
  }

  // Legacy format: @(id:Name)
  const legacyRegex = /@\(([^:]+):([^)]+)\)/g;
  while ((match = legacyRegex.exec(text)) !== null) {
    mentions.push({ userId: match[1], userName: match[2] });
  }

  return mentions;
};
